import moment from "moment";

/**
 * Diccionario de catálogos y nombres amigables para decodificar
 * valores técnicos de la bitácora de cambios.
 */
export const ENTIDADES_NOMBRES = {
    comprobante: "Comprobante",
    comprobantepago: "Pago de Comprobante",
    comprobantedetalle: "Detalle de Comprobante",
    terapiaperiodo: "Periodo de Terapia",
    terapiaperiodopago: "Pago de Periodo de Terapia",
    sesion: "Sesión Clínica",
    sesionterapeuta: "Terapeuta de Sesión",
    sesionparticipante: "Participante de Sesión",
    periodo: "Periodo Académico/Clínico",
    terapia: "Terapia",
    terapiaplan: "Plan de Terapia",
    participante: "Participante",
    persona: "Persona",
    trabajador: "Trabajador / Terapeuta",
    descuento: "Descuento",
    configuracion: "Configuración",
    salon: "Salón",
    ubigeo: "Ubigeo",
};

export const ESTADOS_BASICOS = {
    "1": "Registrado",
    "2": "Activo",
    "3": "Inactivo",
    "4": "Anulado",
};

export const ESTADOS_PAGO = {
    "144": "Pendiente",
    "145": "Cancelado",
    "227": "Pago Consolidado",
};

export const ESTADOS_VALIDACION = {
    "0": "Pendiente",
    "1": "Validada",
    "310": "Validada",
};

/**
 * Extrae el ID principal de la cadena de llaves (ej. "IdComprobante=14" -> "14").
 */
export const extraerIdRegistro = (llaves) => {
    if (!llaves) return "";
    const match = llaves.match(/=([^,;]+)/);
    return match ? match[1].trim() : llaves;
};

/**
 * Formatea un valor técnico (IDs, fechas, montos) a texto comprensible.
 */
export const decodificarValor = (campo, valor) => {
    if (valor == null || valor === "" || valor === "NULL") return "(Vacío / Ninguno)";

    const strValor = String(valor).trim();

    if (campo === "IdEstado") {
        return ESTADOS_BASICOS[strValor] || `Estado ${strValor}`;
    }

    if (campo === "IdEstadoPago") {
        return ESTADOS_PAGO[strValor] || `Estado Pago ${strValor}`;
    }

    if (campo === "IdEstadoValidacion") {
        return ESTADOS_VALIDACION[strValor] || `Validación ${strValor}`;
    }

    // Si es fecha ISO
    if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/.test(strValor)) {
        const m = moment(strValor.replace(" ", "T"));
        if (m.isValid()) {
            return m.format(strValor.includes(":") ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
        }
    }

    // Si es monto con muchos decimales
    if (/^\d+(\.\d+)?$/.test(strValor) && (campo.toLowerCase().includes("monto") || campo.toLowerCase().includes("saldo"))) {
        const n = parseFloat(strValor);
        if (!isNaN(n)) return `S/ ${n.toFixed(2)}`;
    }

    return strValor;
};

/**
 * Analiza un registro de bitácora y devuelve una estructura amigable para el usuario:
 * - tipoAccion: clave de categorización para filtros
 * - tituloAccion: título conciso para badges
 * - colorChip: 'error' | 'success' | 'warning' | 'info' | 'secondary' | 'default'
 * - entidadNombre: nombre de la tabla en español
 * - idRegistro: ID extraído
 * - descripcion: explicación en lenguaje humano de lo ocurrido
 * - cambioLegible: texto del antes y después
 */
export const interpretarOperacion = (fila) => {
    const tabla = (fila.tabla || "").toLowerCase();
    const campo = fila.campo || "";
    const op = fila.operacion || "";
    const ant = fila.valorAnterior;
    const post = fila.valorPosterior;
    const idReg = extraerIdRegistro(fila.llaves);
    const entidad = ENTIDADES_NOMBRES[tabla] || tabla || "Registro";

    // 1. ANULACIONES Y ACTIVACIONES
    if (campo === "IdEstado") {
        if (String(post) === "4") {
            return {
                tipoAccion: "ANULACION",
                tituloAccion: "Anulación",
                colorChip: "error",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se anuló ${entidad} #${idReg}`,
                cambioLegible: `${decodificarValor(campo, ant)} ➔ ${decodificarValor(campo, post)}`,
            };
        }
        if (String(post) === "2") {
            return {
                tipoAccion: "ACTIVACION",
                tituloAccion: "Activación",
                colorChip: "success",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se activó ${entidad} #${idReg}`,
                cambioLegible: `${decodificarValor(campo, ant)} ➔ ${decodificarValor(campo, post)}`,
            };
        }
    }

    // 2. CONSOLIDACIONES Y DESCONSOLIDACIONES DE PAGOS
    if (campo === "IdEstadoPago") {
        if (String(post) === "227") {
            return {
                tipoAccion: "CONSOLIDACION",
                tituloAccion: "Consolidación de Pago",
                colorChip: "secondary",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se consolidó el pago de ${entidad} #${idReg}`,
                cambioLegible: `${decodificarValor(campo, ant)} ➔ Pago Consolidado`,
            };
        }
        if (String(ant) === "227") {
            return {
                tipoAccion: "CONSOLIDACION",
                tituloAccion: "Desconsolidación de Pago",
                colorChip: "warning",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se retiró la consolidación del pago de ${entidad} #${idReg}`,
                cambioLegible: `Pago Consolidado ➔ ${decodificarValor(campo, post)}`,
            };
        }
        if (String(post) === "145") {
            return {
                tipoAccion: "PAGO",
                tituloAccion: "Pago Cancelado",
                colorChip: "success",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `El pago de ${entidad} #${idReg} quedó Cancelado`,
                cambioLegible: `${decodificarValor(campo, ant)} ➔ Cancelado`,
            };
        }
    }

    // 3. ASIGNACIÓN / DESVINCULACIÓN DE COMPROBANTE EN PAGOS DE PERIODOS
    if (tabla === "terapiaperiodopago" && campo === "IdComprobante") {
        const idCompPost = post && post !== "NULL" && post !== "0";
        const idCompAnt = ant && ant !== "NULL" && ant !== "0";

        if (idCompPost) {
            return {
                tipoAccion: "ASIGNACION",
                tituloAccion: "Asignación de Comprobante",
                colorChip: "info",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se asignó el Comprobante #${post} al periodo de terapia`,
                cambioLegible: idCompAnt ? `Comprobante #${ant} ➔ Comprobante #${post}` : `Sin comprobante ➔ Comprobante #${post}`,
            };
        }
        if (!idCompPost && idCompAnt) {
            return {
                tipoAccion: "ASIGNACION",
                tituloAccion: "Desvinculación",
                colorChip: "warning",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se desvinculó el Comprobante #${ant} del periodo de terapia`,
                cambioLegible: `Comprobante #${ant} ➔ (Sin comprobante)`,
            };
        }
    }

    // 3.B. ASIGNACIÓN / LIBERACIÓN DE SALDO EN COMPROBANTE
    if (tabla === "comprobante" && campo === "SaldoDisponible") {
        const valAnt = parseFloat(ant) || 0;
        const valPost = parseFloat(post) || 0;
        if (valPost < valAnt) {
            return {
                tipoAccion: "ASIGNACION",
                tituloAccion: "Asignación de Comprobante",
                colorChip: "info",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Saldo del Comprobante #${idReg} asignado a periodo de terapia`,
                cambioLegible: `Saldo disponible: S/ ${valAnt.toFixed(2)} ➔ S/ ${valPost.toFixed(2)}`,
            };
        }
        if (valPost > valAnt) {
            return {
                tipoAccion: "ASIGNACION",
                tituloAccion: "Liberación de Comprobante",
                colorChip: "warning",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Saldo disponible del Comprobante #${idReg} liberado`,
                cambioLegible: `Saldo disponible: S/ ${valAnt.toFixed(2)} ➔ S/ ${valPost.toFixed(2)}`,
            };
        }
    }

    if (tabla === "terapiaperiodopago" && op === "Alta") {
        return {
            tipoAccion: "ASIGNACION",
            tituloAccion: "Asignación de Periodo",
            colorChip: "info",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Registro / asignación de pago al periodo de terapia #${idReg}`,
            cambioLegible: post ? `Comprobante asociado: #${post}` : `Alta de pago de periodo`,
        };
    }

    // 4. VALIDACIONES Y DESVALIDACIONES DE SESIONES
    if (tabla === "sesion" && (campo === "IdEstadoValidacion" || campo === "UsuarioValidacion")) {
        const esValidada = String(post) === "310" || String(post) === "1";
        if (esValidada) {
            return {
                tipoAccion: "VALIDACION",
                tituloAccion: "Validación de Sesión",
                colorChip: "success",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `La Sesión #${idReg} fue validada`,
                cambioLegible: `Pendiente ➔ Validada`,
            };
        }
        if (String(post) === "0" || String(ant) === "310" || String(ant) === "1") {
            return {
                tipoAccion: "VALIDACION",
                tituloAccion: "Desvalidación de Sesión",
                colorChip: "error",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `La Sesión #${idReg} fue desvalidada`,
                cambioLegible: `Validada ➔ Pendiente`,
            };
        }
    }

    // 5. CIERRE Y APERTURA DE PERIODOS
    if (tabla === "periodo" && (campo === "IdEstadoApertura" || campo.toLowerCase().includes("apertura") || (campo === "IdEstado" && post === "3"))) {
        const esCerrado = String(post).toLowerCase().includes("cerrad") || String(post) === "3";
        if (esCerrado) {
            return {
                tipoAccion: "CIERRE_PERIODO",
                tituloAccion: "Cierre de Periodo",
                colorChip: "error",
                entidadNombre: entidad,
                idRegistro: idReg,
                descripcion: `Se cerró el Periodo #${idReg}`,
                cambioLegible: `Abierto ➔ Cerrado`,
            };
        }
        return {
            tipoAccion: "CIERRE_PERIODO",
            tituloAccion: "Reapertura de Periodo",
            colorChip: "success",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Se reabrió el Periodo #${idReg}`,
            cambioLegible: `Cerrado ➔ Abierto`,
        };
    }

    // 6. DESCUENTOS Y TARIFAS
    if (campo === "IdDescuento" || campo === "MontoDescuento") {
        return {
            tipoAccion: "DESCUENTO",
            tituloAccion: "Descuento",
            colorChip: "primary",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Actualización de descuento en ${entidad} #${idReg}`,
            cambioLegible: `${campo}: ${decodificarValor(campo, ant)} ➔ ${decodificarValor(campo, post)}`,
        };
    }

    if (campo === "IdTarifa" || campo === "MontoTarifa") {
        return {
            tipoAccion: "TARIFA",
            tituloAccion: "Cambio de Tarifa",
            colorChip: "primary",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Asignación de tarifa en ${entidad} #${idReg}`,
            cambioLegible: `${campo}: ${decodificarValor(campo, ant)} ➔ ${decodificarValor(campo, post)}`,
        };
    }

    // 7. ALTAS, BAJAS Y MODIFICACIONES GENERALES
    if (op === "Alta") {
        return {
            tipoAccion: "ALTA",
            tituloAccion: "Alta de Registro",
            colorChip: "primary",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Creación de ${entidad} #${idReg}`,
            cambioLegible: `Nuevo registro creado`,
        };
    }

    if (op === "Baja") {
        return {
            tipoAccion: "BAJA",
            tituloAccion: "Eliminación",
            colorChip: "error",
            entidadNombre: entidad,
            idRegistro: idReg,
            descripcion: `Eliminación de ${entidad} #${idReg}`,
            cambioLegible: `Registro eliminado de la base de datos`,
        };
    }

    // 8. FALLBACK GENERAL
    return {
        tipoAccion: "MODIFICACION",
        tituloAccion: "Modificación",
        colorChip: "default",
        entidadNombre: entidad,
        idRegistro: idReg,
        descripcion: `Actualización de ${campo} en ${entidad} #${idReg}`,
        cambioLegible: `${decodificarValor(campo, ant)} ➔ ${decodificarValor(campo, post)}`,
    };
};

